/**
 * Customer Map — Leaflet + OpenStreetMap only (no Google Maps API key).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Stack } from "@mui/material";
import { ColumnDef } from "@tanstack/react-table";
import { AppBadge, AppButton, AppField, AppInput } from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { OsmMap } from "../../../shared/maps/OsmMap";
import { osmExternalUrl } from "../../../shared/maps/osm";
import { api } from "../../../shared/api/client";

type Marker = {
  id: number;
  customer_number: string;
  name: string;
  phone: string;
  latitude: number;
  longitude: number;
  accuracy_m: number | null;
  area_code: string | null;
  dmrv_status: string | null;
  dmrv_step: number | null;
};

type LoadStatus = "loading" | "error" | "success";

function dmrvBadge(status: string | null) {
  if (!status) return <AppBadge tone="neutral">—</AppBadge>;
  const map: Record<string, { tone: "success" | "warning" | "error" | "info" | "neutral"; label: string }> = {
    verified: { tone: "success", label: "Verified" },
    in_progress: { tone: "info", label: "In Progress" },
    blocked: { tone: "error", label: "Blocked" },
    rejected: { tone: "error", label: "Rejected" },
    not_started: { tone: "neutral", label: "Not Started" },
  };
  const { tone, label } = map[status] ?? { tone: "neutral", label: status };
  return <AppBadge tone={tone}>{label}</AppBadge>;
}

export function CustomerMap() {
  const [markers, setMarkers] = useState<Marker[]>([]);
  const [loadStatus, setLoadStatus] = useState<LoadStatus>("loading");
  const [pageError, setPageError] = useState("");
  const [areaFilter, setAreaFilter] = useState("");
  const [debouncedArea, setDebouncedArea] = useState("");

  const load = useCallback(async () => {
    setLoadStatus("loading");
    try {
      const params = new URLSearchParams(debouncedArea ? { area_code: debouncedArea } : {});
      const query = params.toString();
      const data = await api.get<{ count: number; markers: Marker[] }>(
        `/locations/map-data${query ? `?${query}` : ""}`,
      );
      setMarkers(data.markers ?? []);
      setLoadStatus("success");
    } catch (ex: unknown) {
      setPageError(ex instanceof Error ? ex.message : "Failed to load customer locations");
      setLoadStatus("error");
    }
  }, [debouncedArea]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedArea(areaFilter.trim()), 350);
    return () => clearTimeout(t);
  }, [areaFilter]);

  const pins = useMemo(
    () =>
      markers.map((m) => ({
        lat: m.latitude,
        lng: m.longitude,
        popup: `<strong>${m.name}</strong><br/>${m.customer_number}<br/>${m.phone || ""}`,
        accuracyM: m.accuracy_m,
      })),
    [markers],
  );

  const columns: ColumnDef<Marker, unknown>[] = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "customer_number", header: "Customer #", meta: { hideBelow: "md" } },
    { accessorKey: "phone", header: "Phone", meta: { hideBelow: "md" } },
    { accessorKey: "area_code", header: "Area", meta: { hideBelow: "lg" } },
    {
      id: "dmrv_status",
      header: "dMRV",
      cell: ({ row }) => dmrvBadge(row.original.dmrv_status),
      meta: { hideBelow: "lg" },
    },
    {
      id: "open_map",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <a
          href={osmExternalUrl(row.original.latitude, row.original.longitude)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          Open map ↗
        </a>
      ),
    },
  ];

  const listStatus = loadStatus === "success" && markers.length === 0 ? "empty" : loadStatus;

  return (
    <div>
      <PageHeader
        title="Customer Map"
        subtitle="Customer GPS on OpenStreetMap — no Google Maps key required. Field agents capture location in the app."
        actions={
          <AppButton type="button" variant="secondary" onClick={() => void load()}>
            Refresh
          </AppButton>
        }
      />

      <Stack spacing={2} sx={{ mb: 2 }}>
        <AppField label="Filter by area code">
          <AppInput
            id="map-area-filter"
            type="text"
            placeholder="Area code"
            value={areaFilter}
            onChange={(e) => setAreaFilter(e.target.value)}
            style={{ maxWidth: 240 }}
          />
        </AppField>
      </Stack>

      <PageState
        status={listStatus}
        error={pageError}
        onRetry={() => void load()}
        emptyTitle="No mapped customers"
        emptyHint="Customers need GPS coordinates from registration or installation."
      >
        <OsmMap pins={pins} />
        <div style={{ marginTop: 16 }}>
          <AppDataTable data={markers} columns={columns} />
        </div>
      </PageState>
    </div>
  );
}
